import { Stack, useTheme } from "@mui/material";
import {
	CircleCheck,
	Clock,
	type LucideIcon,
	Pencil,
	Trash2,
	Undo2,
} from "lucide-react";
import { toast } from "sonner";
import { useReminderCancelSnoozeMutation } from "web_myplann/Api/Reminders/useReminderCancelSnoozeMutation";
import { useReminderResolveMutation } from "web_myplann/Api/Reminders/useReminderResolveMutation";
import { ReminderStatusEnum } from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import type {
	Reminder,
	ResourceKey,
} from "web_myplann/Reminders/Types/ReminderTypes";
import { ListRow } from "web_myplann/Shared/Components/ListRow";
import { MyPlannDrawer } from "web_myplann/Shared/Components/MyPlannDrawer";
import { parseApiDate } from "web_myplann/Shared/Utils/apiDate";
import { dateFormatter } from "web_myplann/Shared/Utils/dateFormatters";

type Props = {
	reminder: Reminder;
	open: boolean;
	onEdit: () => void;
	onSnooze: () => void;
	onDelete: () => void;
	onClose: () => void;
};

type ActionRow = {
	id: string;
	labelKey: ResourceKey;
	icon: LucideIcon;
	onClick: () => void;
	destructive?: boolean;
};

export const ReminderActionsDrawer = ({
	reminder,
	open,
	onEdit,
	onSnooze,
	onDelete,
	onClose,
}: Props) => {
	const { t } = useResource();
	const theme = useTheme();

	const { mutate: resolveReminder } = useReminderResolveMutation();
	const { mutate: cancelSnooze } = useReminderCancelSnoozeMutation();

	const isSnoozed = reminder.status === ReminderStatusEnum.Snoozed;

	const handleResolve = () => {
		resolveReminder(reminder.id, {
			onSuccess: ({ nextOccurrence }) => {
				toast.success(
					nextOccurrence
						? t(Resources.Reminders.Resolve.nextScheduledToast, {
								name: nextOccurrence.title,
								date: dateFormatter(parseApiDate(nextOccurrence.date)),
							})
						: t(Resources.Reminders.Resolve.resolvedToast),
				);
				onClose();
			},
		});
	};

	const handleCancelSnooze = () => {
		cancelSnooze(reminder.id, { onSuccess: onClose });
	};

	const stateAction: ActionRow = isSnoozed
		? {
				id: "cancelSnooze",
				labelKey: Resources.Reminders.Actions.cancelSnooze,
				icon: Undo2,
				onClick: handleCancelSnooze,
			}
		: {
				id: "snooze",
				labelKey: Resources.Reminders.Actions.snooze,
				icon: Clock,
				onClick: onSnooze,
			};

	const actions: ActionRow[] = [
		{
			id: "resolve",
			labelKey: Resources.Reminders.Actions.resolve,
			icon: CircleCheck,
			onClick: handleResolve,
		},
		stateAction,
		{
			id: "edit",
			labelKey: Resources.Reminders.Actions.edit,
			icon: Pencil,
			onClick: onEdit,
		},
		{
			id: "delete",
			labelKey: Resources.Reminders.Actions.remove,
			icon: Trash2,
			onClick: onDelete,
			destructive: true,
		},
	];

	return (
		<MyPlannDrawer open={open} onClose={onClose} grabber title={reminder.name}>
			<Stack pb={theme.spacingTokens.sm}>
				{actions.map((action) => {
					const Icon = action.icon;
					return (
						<ListRow
							key={action.id}
							icon={
								<Icon
									size={18}
									color={
										action.destructive
											? theme.palette.red[500]
											: theme.palette.grey[600]
									}
									aria-hidden
								/>
							}
							title={t(action.labelKey)}
							destructive={action.destructive}
							onClick={action.onClick}
							isLast
						/>
					);
				})}
			</Stack>
		</MyPlannDrawer>
	);
};
